import React, { useEffect, useState } from "react";
import { uploads as uploadsApi } from "../services/api";

// GCS-uploaded files (helper photos, ID proofs, etc.) live in a private bucket —
// a plain <img src="https://storage.googleapis.com/..."> gets a 403 since the
// browser can't attach the app's auth header to an <img> request. This fetches
// the file through the authenticated download proxy instead and renders it as
// an object URL. Cached by fileUrl so the same image isn't re-fetched on re-render.
const objectUrlCache = new Map<string, string>();

interface Props extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  societyId: string;
  fileUrl: string | undefined | null;
  fallback?: React.ReactNode;
}

const AuthedImage: React.FC<Props> = ({ societyId, fileUrl, fallback = null, ...imgProps }) => {
  const [src, setSrc] = useState<string | undefined>(fileUrl ? objectUrlCache.get(fileUrl) : undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (!fileUrl) {
      setSrc(undefined);
      return;
    }
    const cached = objectUrlCache.get(fileUrl);
    if (cached) {
      setSrc(cached);
      return;
    }
    setSrc(undefined);
    let cancelled = false;
    uploadsApi
      .download(societyId, fileUrl)
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlCache.set(fileUrl, objectUrl);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [societyId, fileUrl]);

  if (!fileUrl || failed) return <>{fallback}</>;
  if (!src) return null;
  return <img src={src} {...imgProps} />;
};

export default AuthedImage;
